import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_CODE_CONTAINER_REQUEST,
  GET_CREDENTIAL_REQUEST,
  GET_DATA_MAP_REQUEST,
  GET_DATA_SET_REQUEST,
  GET_DATA_SOURCE_REQUEST,
  GET_FLOW_METRICS_REQUEST,
  GET_PIPELINE_REQUEST,
  GET_PIPELINES_REQUEST,
  GET_SINK_REQUEST,
} from 'js/constants/actionTypes';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  RESOURCE_TYPE_CREDENTIALS,
  RESOURCE_TYPE_DATA_MAP,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_RECORD_TRANSFORM,
  RESOURCE_TYPE_SCHEMA_SPECS,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { getResourceEntity } from 'js/lib/PipelineUtils';

function getFlow({ id, entities }) {
  const { flowNodes, users } = entities;

  let data = getResourceEntity(flowNodes[id], entities, true, ['name', 'description']);

  if (!data) {
    return null;
  }

  if (data.owner_id) {
    data.owner = users[data.owner_id];
  }

  return data;
}

export function useResource(resourceType: string, id: string) {
  const entities = useSelector((state: any) => state.entities);
  const { credentials, sources, schemaSpecs, dataMaps, sinks, dataSets, users, transforms } =
    entities;

  const loading = useIsLoading(
    GET_PIPELINE_REQUEST,
    GET_PIPELINES_REQUEST,
    GET_CREDENTIAL_REQUEST,
    GET_DATA_SOURCE_REQUEST,
    GET_DATA_MAP_REQUEST,
    GET_SINK_REQUEST,
    GET_DATA_SET_REQUEST,
    GET_CODE_CONTAINER_REQUEST,
  );

  return useMemo(() => {
    if (!resourceType) {
      return { resource: null, loading: false };
    }
    if (!id) {
      return { resource: null, loading: false };
    }
    const resource = (() => {
      if (resourceType === RESOURCE_TYPE_FLOW) {
        return getFlow({ id, entities });
      }
      if (resourceType === RESOURCE_TYPE_DATA_SET) {
        return dataSets?.[id];
      }
      if (resourceType === RESOURCE_TYPE_SOURCE) {
        return sources?.[id];
      }
      if (resourceType === RESOURCE_TYPE_CREDENTIALS) {
        return credentials?.[id];
      }
      if (resourceType === RESOURCE_TYPE_DATA_MAP) {
        return dataMaps?.[id];
      }
      if (resourceType === RESOURCE_TYPE_SINK) {
        return sinks?.[id];
      }
      if (resourceType === RESOURCE_TYPE_SCHEMA_SPECS) {
        return schemaSpecs?.[id];
      }
      if (resourceType === RESOURCE_TYPE_RECORD_TRANSFORM) {
        return transforms?.[id];
      }
      return null;
    })();

    return { loading, resource };
  }, [loading, resourceType, id, dataMaps, dataSets, sources, users, schemaSpecs, sinks]);
}
